import React from 'react';
import { Grid, Button, Divider } from '@mui/material';
import { Forest, CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { BoolField } from '../../CommonComponents/Form';
import { SectionDivider } from '../../CommonComponents';
import { ReactComponent as FairTransportIcon } from '../../../assets/icons/fairtransport-icon.svg';
import METADATA_TYPES from '../../../types/MetadataType';
import { hasStandardProperty, changeStandardProperty, fairtransportMembershipOptions } from '../../../utils/metadata';

interface SustainabilityBlockProps {
    entity: any;
    onChange: (e: any) => void;
    settables: string[];
}

const SustainabilityBlock = ({
    entity,
    onChange,
    settables
}: SustainabilityBlockProps) => {
    const [selected, setSelected] = React.useState<'all' | 'some' | 'none'>('some');

    const selectAll = () => {
        const currentSust = entity.sustainability || [];
        const newSust = Array.from(new Set([...currentSust, ...settables]));
        onChange({ ...entity, sustainability: newSust });
    };

    const selectNone = () => {
        const currentSust = entity.sustainability || [];
        const newSust = currentSust.filter((sust: string) => !settables.includes(sust));
        onChange({ ...entity, sustainability: newSust });
    };

    const changeFairtransportMembership = (value: typeof fairtransportMembershipOptions[number]['value']) => {
        onChange({
            ...entity,
            fairtransportMembership: value
        });
    };

    React.useEffect(() => {
        if (settables.every((sust) => entity.sustainability?.includes(sust))) {
            setSelected('all');
        } else if ((entity.sustainability?.length ?? 0) === 0) {
            setSelected('none');
        } else {
            setSelected('some');
        }
    }, [entity, settables]);

    return (
        <Grid item lg={12} xl={6}>
            <SectionDivider
                label={`Hållbarhet (${entity.sustainability?.length || 0})`}
                icon={<Forest fontSize="small" />}
                marginTop={2}
                marginBottom={0}
            />
            <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => selectAll()}
                        startIcon={selected === 'all' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                        sx={{ fontWeight: 300 }}
                    >
                        Alla
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => selectNone()}
                        startIcon={selected === 'none' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                        sx={{ fontWeight: 300 }}
                    >
                        Inga
                    </Button>
                </Grid>
            </Grid>
            <Divider sx={{ mb: 2, width: '160px' }} />
            <Grid container spacing={1}>
                {settables.map((sust: string) => (
                    <Grid item xs={4} key={sust}>
                        <BoolField
                            name={sust}
                            label={sust}
                            value={hasStandardProperty({ array: entity.sustainability, value: sust })}
                            onChange={() => changeStandardProperty({
                                key: METADATA_TYPES.SUSTAINABILITY,
                                array: entity.sustainability,
                                value: sust,
                                entity,
                                onChange
                            })}
                        />
                    </Grid>
                ))}
            </Grid>

            <SectionDivider
                label="Fair Transport-medlemsskap"
                icon={<FairTransportIcon style={{ height: 20, width: 20, color: 'inherit' }} />}
                marginTop={2}
                marginBottom={2}
            />
            <Grid container spacing={1}>
                {fairtransportMembershipOptions.map((membershipOption) => (
                    <Grid item xs={12} sm={6} key={membershipOption.value}>
                        <BoolField
                            name={membershipOption.value}
                            label={membershipOption.label}
                            value={(entity.fairtransportMembership || 'not-member') === membershipOption.value}
                            onChange={() => changeFairtransportMembership(membershipOption.value)}
                        />
                    </Grid>
                ))}
            </Grid>
        </Grid>
    );
};

export default React.memo(SustainabilityBlock);
